'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowRight, Megaphone, Sparkles, Store, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import type { MarketingTactic } from '@sizzlenz/shared';
import { FeedCarousel } from '@/components/ui/feed-carousel';
import { ExampleCard, contentToDemandExample } from '@/components/demand/ExampleCard';
import { useContentDetailSheet } from '@/lib/hooks/useContentDetailSheet';
import { useContentList } from '@/lib/hooks/useContent';
import { buildInspirationFilterSystemTags, buildInspirationFeedHref } from '@/lib/inspiration-filters';
import type { ApiContent } from '@/lib/api/types';
const COLLABORATIONS_HUB_HREF = '/content/collaborations-central';

/** How many inspiration examples the collaboration carousel shows. */
const MAX_FEED_ITEMS = 8;

/** Default tactic(s) the inspiration feed carousel is locked to. */
const DEFAULT_LOCKED_TACTICS: readonly MarketingTactic[] = ['collaboration'];
interface CollaborationType {
  icon: LucideIcon;
  title: string;
  description: string;
}

/**
 * A teaser subset of the collaboration types shown in full on the Collaborations
 * hub (see CollaborationsInfo). Kept short so the home section stays scannable.
 */
const COLLABORATION_TYPES: CollaborationType[] = [{
  icon: Megaphone,
  title: 'Co-branded campaigns',
  description: 'Joint marketing that combines two or more brands to reach wider audiences and share the costs.'
}, {
  icon: Sparkles,
  title: 'Collaborative launches',
  description: 'Limited-edition co-created products, flavour collaborations, and joint innovation projects.'
}, {
  icon: Users,
  title: 'Shared activations',
  description: 'Multi-brand pop-ups, joint tastings, and shared events that pool resources for bigger impact.'
}, {
  icon: Store,
  title: 'Retail & distribution',
  description: 'Joint retail placements, shared shelf space, and distribution tie-ups that expand your reach.'
}];
interface HomeCollaborationsSectionProps {
  /**
   * Show the informational collaboration-type cards. Defaults to true.
   */
  showCards?: boolean;
  /**
   * Show the inspiration feed carousel below the cards. Defaults to true.
   */
  showFeed?: boolean;
  /**
   * Tactic(s) the inspiration feed carousel is locked to. Defaults to
   * `['collaboration']`. These are applied server-side as a coarse OR prefilter
   * (see buildInspirationFilterSystemTags) so the carousel only surfaces
   * collaboration-tagged inspiration.
   */
  lockedTactics?: readonly MarketingTactic[];
  /**
   * Label shown above the inspiration feed carousel. Defaults to
   * `'Some Examples'`.
   */
  feedLabel?: string;
}
export function HomeCollaborationsSection(t0) {
  const $ = _c(29);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    showCards: t2,
    showFeed: t3,
    lockedTactics: t4,
    feedLabel: t5
  } = t1;
  const showCards = t2 === undefined ? true : t2;
  const showFeed = t3 === undefined ? true : t3;
  const lockedTactics = t4 === undefined ? DEFAULT_LOCKED_TACTICS : t4;
  const feedLabel = t5 === undefined ? "Examples" : t5;
  const {
    showContentDetail
  } = useContentDetailSheet();
  let t6;
  if ($[2] !== lockedTactics) {
    t6 = buildInspirationFilterSystemTags({
      demandRoles: [],
      journeyStages: [],
      productStages: [],
      demandObjectives: [],
      tactics: [...lockedTactics],
      channels: [],
      deliveryActivities: [],
      collaborationModels: [],
      activationFormats: []
    });
    $[2] = lockedTactics;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const systemTags = t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["inspiration", "case-study"];
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const t8 = systemTags.length > 0 ? systemTags : undefined;
  let t9;
  if ($[5] !== t8) {
    t9 = {
      type: t7,
      status: "published",
      limit: MAX_FEED_ITEMS,
      systemTags: t8
    };
    $[5] = t8;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== showFeed) {
    t10 = {
      enabled: showFeed
    };
    $[7] = showFeed;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const {
    data
  } = useContentList(t9, t10);
  let t11;
  if ($[9] !== data?.pages?.[0]?.content) {
    t11 = data?.pages?.[0]?.content ?? [];
    $[9] = data?.pages?.[0]?.content;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t11) {
    t12 = t11.slice(0, MAX_FEED_ITEMS);
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const feedItems = t12;
  let t13;
  let t14;
  let t15;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mb-2"><h2 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">Do Collaborations to Reach New Customers and Stand Out</h2></div>;
    t14 = <p className="text-lg text-gray-500 mb-4 max-w-2xl">Almost everyone is sleeping on collaborations!</p>;
    t15 = <p className="text-lg text-gray-500 mb-4">Team up with other brands, retailers, influencers to reach new audiences and get more attention and awareness.</p>;
    $[13] = t13;
    $[14] = t14;
    $[15] = t15;
  } else {
    t13 = $[13];
    t14 = $[14];
    t15 = $[15];
  }
  let t16;
  if ($[16] !== showCards) {
    t16 = showCards && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{COLLABORATION_TYPES.map(_temp)}</div>;
    $[16] = showCards;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] !== t16) {
    t17 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t13}{t14}{t15}{t16}</div>;
    $[18] = t16;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== feedItems || $[21] !== feedLabel || $[22] !== showContentDetail || $[23] !== showFeed) {
    t18 = showFeed && feedItems.length > 0 && <>{feedLabel && <div className="max-w-7xl mx-auto mt-12 px-4 sm:px-6 lg:px-8"><h3 className="text-xl font-bold tracking-tight text-gray-900">{feedLabel}</h3></div>}<div className="max-w-7xl mx-auto"><FeedCarousel className="mt-6 text-center" exploreMore={{
          href: "/content/collaborations-central?tab=inspiration",
          label: "View More Collaboration Inspiration"
        }}>{feedItems.map(content => <ExampleCard key={content.id} variant="compact" example={contentToDemandExample(content)} detailMode="sheet" onOpen={() => showContentDetail(content)} moreDetailsLabel="Details" demandFacets={[]} sections={[]} />)}</FeedCarousel></div></>;
    $[20] = feedItems;
    $[21] = feedLabel;
    $[22] = showContentDetail;
    $[23] = showFeed;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><div className="mt-10 text-center"><Link href={COLLABORATIONS_HUB_HREF} className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#ff5a36] px-7 py-3.5 text-base font-black text-white shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-500/30">Go to Collaborations Central<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link></div></div>;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t17 || $[27] !== t18) {
    t20 = <section className="py-16">{t17}{t18}{t19}</section>;
    $[26] = t17;
    $[27] = t18;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  return t20;
}
function _temp(t0) {
  const {
    icon: Icon,
    title,
    description
  } = t0;
  return <div key={title} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"><div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 mb-4"><Icon className="w-6 h-6" /></div><h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3><p className="text-sm text-gray-600 leading-relaxed">{description}</p></div>;
}